import { useEffect, useMemo, useState, useCallback } from "react";
import { useQuery, useMutation } from "@apollo/client";
import moment from "moment";
import { getAIChatById, getAIChats } from "../../../../queries/ai.graphql";
import {
  upsertAIChat,
  upsertAIChatMessage,
} from "../../../../queries/mutations/aiChatMutations.graphql";

/**
 * @typedef {import('../../../../components/AIContent/types').Message} Message
 * @typedef {import('../../../../components/AIContent/types').CreateChatMessage} CreateChatMessage
 */

/**
 * @param {string} chatId - The ID of the chat to get
 * @returns {Object} The chat data
 */
const useGetAIChat = () => {
  const [chatId, setChatId] = useState(null);
  const [currentChat, setCurrentChat] = useState(null);

  const {
    data: chatsData,
    loading: chatsLoading,
    error: chatsError,
    refetch: refetchChats,
  } = useQuery(getAIChats, {});

  const chats = useMemo(() => chatsData?.chats || [], [chatsData?.chats]);

  const sortedChats = useMemo(() => {
    if (!Array.isArray(chats) || chats.length === 0) {
      return [];
    }
    // Sort by 'createdAt' or 'updatedAt' in descending order (most recent first)
    return [...chats].sort((a, b) => {
      const dateA = moment(parseInt(a.updatedAt || a.createdAt, 10)).diff(
        moment()
      );
      const dateB = moment(parseInt(b.updatedAt || b.createdAt, 10)).diff(
        moment()
      );

      return dateB - dateA;
    });
  }, [chats]);

  const {
    data: currentChatData,
    loading: chatLoading,
    error: chatError,
    updateQuery,
  } = useQuery(getAIChatById, {
    variables: { id: chatId },
    skip: !chatId,
    fetchPolicy: "network-only",
  });

  const [upsertAIChatMutation] = useMutation(upsertAIChat);
  const [upsertAIChatMessageMutation] = useMutation(upsertAIChatMessage);

  const updateChatOptimistically = useCallback((message) => {
    setCurrentChat((prevChat) => {
      const hasMessage = prevChat?.messages?.some((m) => m.id === message.id);

      const updatedMessages = hasMessage
        ? prevChat.messages.map((m) => (m.id === message.id ? message : m))
        : [...prevChat.messages, message];

      return {
        ...prevChat,
        messages: updatedMessages,
      };
    });
  }, []);

  /**
   * @type {CreateChatMessage}
   */
  const onUpsertAIChatMessage = useCallback(
    async (message) => {
      if (!chatId) {
        return null;
      }

      const result = await upsertAIChatMessageMutation({
        variables: {
          input: {
            ...message,
            aiChatId: chatId,
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          message: {
            __typename: "AIChatMessage",
            id: message.id,
            aiChatId: chatId,
            role: message.role,
            content: message.content,
            status: message.status,
            citations: message.citations,
            respondsTo: message.respondsTo,
            tools: message.tools,
          },
        },
        update: (proxy, response) => {
          const newMessage = response.data.message;

          updateChatOptimistically(newMessage);
          // Update title of chat if user message is added for updating the title in ChatHistoryPanel.
          if (message.role === "user") {
            updateQuery((prevQuery) => {
              return {
                chat: {
                  ...prevQuery.chat,
                  title: message.content,
                },
              };
            });
          }
        },
      });

      return result;
    },
    [chatId, upsertAIChatMessageMutation, updateChatOptimistically, updateQuery]
  );

  const onStartNewChat = useCallback(() => {
    setChatId(null);
    setCurrentChat(null);

    upsertAIChatMutation({
      variables: { input: { id: null } },
    }).then((res) => {
      setChatId(res.data.chat.id);
      refetchChats();
    });
  }, [setChatId, setCurrentChat, upsertAIChatMutation, refetchChats]);

  const onSelectChat = useCallback(
    (selectedChatId) => {
      setChatId(selectedChatId);
    },
    [setChatId]
  );

  useEffect(() => {
    if (currentChatData && currentChat?.id !== currentChatData.chat.id) {
      setCurrentChat(currentChatData.chat);
    }
  }, [currentChatData, currentChat]);

  useEffect(() => {
    if (chatsData && chats.length && !chatId) {
      setChatId(chats[0].id);
    }
  }, [chatsData, chats, chatId]);

  useEffect(() => {
    if (!chatsLoading && !chats.length && !chatId) {
      upsertAIChatMutation({
        variables: { input: { id: null } },
      }).then((res) => {
        setChatId(res.data.chat.id);
        refetchChats();
      });
    }
  }, [chatsLoading, chats, upsertAIChatMutation, chatId, refetchChats]);

  return {
    chatId,
    chats: sortedChats,
    currentChat,
    loading: chatsLoading || chatLoading,
    error: chatsError || chatError,
    onUpsertAIChatMessage,
    updateChatOptimistically,
    onStartNewChat,
    onSelectChat,
  };
};

export default useGetAIChat;
